import { useState } from 'react'
import { twJoin } from 'tailwind-merge'

import { getWorkflowTypeColor } from '@routes/Workflow/util'
import { type WorkflowJobResponse, type WorkflowResponse } from '@state/workflow.ts'

function getColor(status: WorkflowJobResponse['status']) {
  if (status === 'RUNNING') {
    return 'bg-blue-500 text-white hover:bg-blue-600'
  } else if (status === 'FAILED') {
    return 'bg-red-500 text-white hover:bg-red-600'
  }

  return 'bg-green-500 text-white hover:bg-green-600'
}

function getWorkflowColorByStatus(status: WorkflowResponse['status'], workflowType: string) {
  // If workflow failed, always show red
  if (status === 'FAILED') {
    return 'bg-red-500 text-white hover:bg-red-600'
  }

  // Otherwise use workflow type color with status indicators
  const typeColor = getWorkflowTypeColor(workflowType)

  if (status === 'RUNNING') {
    // Add a subtle animation or border for running workflows
    return `${typeColor.bg} text-white ${typeColor.hover} ring-2 ring-blue-400 ring-opacity-50`
  }

  return `${typeColor.bg} text-white ${typeColor.hover}`
}

interface WorkflowOrJobBarsProps {
  workflow: WorkflowResponse
  startTime: number
  duration: number
  selectWorkflow: (workflowId: string) => void
}

function WorkflowOrJobBars({
  workflow,
  startTime,
  duration,
  selectWorkflow,
}: WorkflowOrJobBarsProps) {
  // eslint-disable-next-line react-hooks/purity
  const now = Date.now()
  const [isToggled, setIsToggled] = useState(false)

  const workflowStart = new Date(workflow.createdAt).getTime()
  const workflowEnd = workflow.status === 'RUNNING' ? now : new Date(workflow.updatedAt).getTime()
  const leftPercent = ((workflowStart - startTime) / duration) * 100
  const widthPercent = ((workflowEnd - workflowStart) / duration) * 100

  function handleClick() {
    selectWorkflow(workflow.id)
    setIsToggled(!isToggled)
  }

  return (
    <div
      className='absolute inset-y-0'
      style={{
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
      }}
      onClick={handleClick}
    >
      <Workflow workflow={workflow} overlayMode={isToggled} />
      {isToggled && (
        <Jobs jobs={workflow.jobs} workflowStart={workflowStart} workflowEnd={workflowEnd} />
      )}
    </div>
  )
}

interface WorkflowProps {
  workflow: WorkflowResponse
  overlayMode: boolean
}

function Workflow({ workflow, overlayMode }: WorkflowProps) {
  const color = getWorkflowColorByStatus(workflow.status, workflow.workflow)

  const className = twJoin(
    'flex size-full cursor-pointer items-center overflow-hidden rounded-sm text-xs font-medium transition-opacity',
    color,
    overlayMode ? 'opacity-20' : ''
  )

  const displayText = workflow.name ?? workflow.workflow

  return (
    <div
      className={className}
      title={`${workflow.name ?? workflow.workflow} (${workflow.status}) - Click to view jobs`}
    >
      <span className="truncate px-1">
        {!overlayMode && displayText}
      </span>
    </div>
  )
}

interface JobsProps {
  jobs: WorkflowJobResponse[]
  workflowStart: number
  workflowEnd: number
}

function Jobs({ jobs, workflowStart, workflowEnd }: JobsProps) {
  // eslint-disable-next-line react-hooks/purity
  const now = Date.now()
  const duration = workflowEnd - workflowStart

  return (
    <>
      {jobs.map((job) => {
        const color = getColor(job.status)

        const jobStart = job.startedAt ? new Date(job.startedAt).getTime() : workflowStart
        // Use current time for running jobs, otherwise use completedAt or workflow end
        const jobEnd = job.status === 'RUNNING'
          ? now
          : job.completedAt
            ? new Date(job.completedAt).getTime()
            : workflowEnd
        const jobLeftPercent = ((jobStart - workflowStart) / duration) * 100
        const jobWidthPercent = ((jobEnd - jobStart) / duration) * 100

        return (
          <div
            key={job.id}
            className={`
              absolute inset-y-1 flex items-center overflow-hidden rounded-sm
              text-xs font-medium
              ${color}
            `}
            style={{
              left: `${jobLeftPercent}%`,
              width: `${jobWidthPercent}%`,
            }}
            title={`${job.jobName} (${job.status}) - Click to collapse`}
          />
        )
      })}
    </>
  )
}

export default WorkflowOrJobBars